import { cn } from '@/lib/utils';

/**
 * Base loading shape. Paints the shared-phase sheen (see the `skeleton-sheen`
 * utility in `index.css`) and collapses to a flat tint under reduced motion.
 *
 * Prefer the typed helpers below over raw `<Skeleton className="h-3 w-40" />`:
 * they encode the dimensions of the real component being stood in for, which is
 * what keeps the loading → content swap free of layout shift.
 */
function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="skeleton"
            aria-hidden="true"
            className={cn(
                'skeleton-sheen rounded-md motion-reduce:animate-none motion-reduce:bg-none',
                className,
            )}
            {...props}
        />
    );
}

/**
 * Type scale for text placeholders, mirroring Tailwind's font-size/line-height
 * pairs as used across the product.
 *
 * `box` is the line box the real text will occupy — reserving it is what stops
 * the content swap from shifting anything below. `bar` is roughly the cap
 * height of that text, and is all we actually paint: a placeholder drawn at
 * full line-box height reads far heavier than the text it replaces and makes a
 * loading screen look like a barcode.
 */
const TEXT_SIZES = {
    /** text-xs — 12px/16px. Table meta, captions, descriptions. */
    xs: { box: 16, bar: 8 },
    /** text-sm — 14px/20px. Body default, table cells, labels. */
    sm: { box: 20, bar: 10 },
    /** text-base — 16px/24px. */
    base: { box: 24, bar: 11 },
    /** text-lg — 18px/28px. CardTitle. */
    lg: { box: 28, bar: 12 },
    /** text-xl font-bold — 20px/28px. Detail-page h1. */
    xl: { box: 28, bar: 14 },
    /** text-2xl font-bold — 24px/32px. Module-header h1, StatValue. */
    h1: { box: 32, bar: 16 },
} as const;

type SkeletonTextSize = keyof typeof TEXT_SIZES;

interface SkeletonTextProps extends Omit<React.ComponentProps<'div'>, 'children'> {
    /** Which step of the type scale this line stands in for. */
    readonly size?: SkeletonTextSize;
    /** CSS width for the painted bar — e.g. `'40%'` or `'160px'`. Defaults to full. */
    readonly width?: string;
}

/**
 * A single line of placeholder text, sized to the type scale.
 *
 * Renders a full-height line box containing a cap-height bar, so it measures
 * like the text it replaces while reading as much lighter on the page.
 */
function SkeletonText({ size = 'sm', width, className, style, ...props }: SkeletonTextProps) {
    const { box, bar } = TEXT_SIZES[size];
    return (
        <div
            data-slot="skeleton-text"
            aria-hidden="true"
            className={cn('flex items-center', className)}
            style={{ height: box, ...style }}
            {...props}
        >
            <Skeleton
                className="w-full rounded-sm"
                style={{ height: bar, ...(width ? { width } : {}) }}
            />
        </div>
    );
}

/** Widths that decay down a paragraph, so multi-line blocks don't read as a slab. */
const PARAGRAPH_WIDTHS = ['100%', '96%', '88%', '72%'] as const;

interface SkeletonParagraphProps extends Omit<React.ComponentProps<'div'>, 'children'> {
    readonly lines?: number;
    readonly size?: SkeletonTextSize;
}

/** A run of text lines with a ragged right edge, like real copy. */
function SkeletonParagraph({
    lines = 3,
    size = 'sm',
    className,
    ...props
}: SkeletonParagraphProps) {
    return (
        <div data-slot="skeleton-paragraph" className={cn('flex flex-col', className)} {...props}>
            {Array.from({ length: lines }).map((_, i) => (
                <SkeletonText
                    key={i}
                    size={size}
                    width={PARAGRAPH_WIDTHS[i % PARAGRAPH_WIDTHS.length]}
                />
            ))}
        </div>
    );
}

interface SkeletonBadgeProps extends React.ComponentProps<'div'> {
    /** Badges are content-sized; pass the width of the status word being stood in for. */
    readonly width?: number;
}

/** Matches `Badge` / status pills — 22px tall, fully rounded. */
function SkeletonBadge({ width = 68, className, style, ...props }: SkeletonBadgeProps) {
    return (
        <Skeleton
            data-slot="skeleton-badge"
            className={cn('h-[22px] shrink-0 rounded-full', className)}
            style={{ width, ...style }}
            {...props}
        />
    );
}

/** Control heights from the design system: sm 32px, default 36px, lg 40px. */
const BUTTON_HEIGHTS = { sm: 32, default: 36, lg: 40 } as const;

interface SkeletonButtonProps extends React.ComponentProps<'div'> {
    readonly size?: keyof typeof BUTTON_HEIGHTS;
    /** CSS width — buttons are content-sized, so callers pass the real label width. */
    readonly width?: number | string;
}

/** Matches `Button` at the shared form-control sizes. */
function SkeletonButton({
    size = 'default',
    width = 104,
    className,
    style,
    ...props
}: SkeletonButtonProps) {
    return (
        <Skeleton
            data-slot="skeleton-button"
            className={cn('shrink-0 rounded-md', className)}
            style={{ height: BUTTON_HEIGHTS[size], width, ...style }}
            {...props}
        />
    );
}

/** Matches `Avatar` — circular, sized in px. */
function SkeletonAvatar({
    size = 32,
    className,
    style,
    ...props
}: React.ComponentProps<'div'> & { readonly size?: number }) {
    return (
        <Skeleton
            data-slot="skeleton-avatar"
            className={cn('shrink-0 rounded-full', className)}
            style={{ height: size, width: size, ...style }}
            {...props}
        />
    );
}

/**
 * Matches the rounded icon tile that opens every module header
 * (`size-14 rounded-xl bg-bg-icon-tile`).
 */
function SkeletonIconTile({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <Skeleton
            data-slot="skeleton-icon-tile"
            className={cn('size-14 shrink-0 rounded-xl', className)}
            {...props}
        />
    );
}

interface SkeletonBoundaryProps extends React.ComponentProps<'div'> {
    /** Announced to assistive tech while the region loads. */
    readonly label?: string;
}

/**
 * Wraps a loading region. The shapes inside are all `aria-hidden`, so this
 * supplies the one thing a screen reader should hear: that the region is busy.
 */
function SkeletonBoundary({
    label = 'Loading',
    className,
    children,
    ...props
}: SkeletonBoundaryProps) {
    return (
        <div
            data-slot="skeleton-boundary"
            role="status"
            aria-busy="true"
            className={className}
            {...props}
        >
            <span className="sr-only">{label}</span>
            {children}
        </div>
    );
}

export {
    Skeleton,
    SkeletonAvatar,
    SkeletonBadge,
    SkeletonBoundary,
    SkeletonButton,
    SkeletonIconTile,
    SkeletonParagraph,
    SkeletonText,
    type SkeletonTextSize,
};
